/**
 * AI Studio — every AI tool in one searchable grid.
 * All tools deep-link to existing live implementations.
 */
import { Sparkles, Search, FileText, Tag, Image as ImageIcon, Hash, BarChart3, Type,
  Youtube, Pen, GraduationCap, Mail, Megaphone, MessageSquare,
  LayoutGrid, Wand2, FileSearch, Calculator, Palette, Key, Link as LinkIcon,
  Network, Mic,
} from "lucide-react";
import SuiteHub from "@/components/premium/SuiteHub";

const SECTIONS = [
  {
    title: "SEO & keywords",
    tools: [
      { name: "AI Keyword Generator", desc: "Live SerpAPI + AI suggestions", Icon: Search, to: "/tools/seo/ai?tool=keyword-generator", badge: "LIVE DATA" },
      { name: "SEO Content Grader", desc: "Claude AI scores your article", Icon: FileSearch, to: "/tools/seo/ai?tool=content-grader", badge: "LIVE" },
      { name: "Meta Tag Generator", desc: "Title + description optimized", Icon: Tag, to: "/tools/seo/ai?tool=meta-tags", badge: "LIVE" },
      { name: "Topical Map", desc: "Cluster + pillar architecture", Icon: Network, to: "/tools/seo/ai?tool=keyword-clustering", badge: "LIVE" },
      { name: "SERP Simulator", desc: "Preview your Google listing", Icon: Search, to: "/tools/seo/ai?tool=serp-simulator", badge: "LIVE" },
      { name: "Technical SEO", desc: "Site audit + schema + sitemap", Icon: BarChart3, to: "/tools/seo/technical", badge: "LIVE" },
    ],
  },
  {
    title: "YouTube",
    tools: [
      { name: "Channel Assessment", desc: "Audit any channel — 25 metrics", Icon: Youtube, to: "/youtube-suite/channel-assessment", badge: "NEW" },
      { name: "YouTube SEO Tools", desc: "Title + description + tags + hashtags", Icon: Sparkles, to: "/tools/youtube", badge: "LIVE" },
      { name: "AI SEO Assistant", desc: "Gemini 3 — full video optimization", Icon: FileText, to: "/studio/seo", badge: "LIVE" },
      { name: "Auto Captions", desc: "Upload → branded SRT + share page", Icon: Mic, to: "/tools/auto-captions", badge: "LIVE" },
      { name: "Thumbnail Generator", desc: "AI thumbnail concepts", Icon: ImageIcon, to: "/ai-image", badge: "LIVE" },
      { name: "Brand Kit", desc: "Logo + colors + fonts", Icon: Palette, to: "/brand-kit", badge: "LIVE" },
    ],
  },
  {
    title: "Content & social",
    tools: [
      { name: "Social Media Tools", desc: "Instagram / FB / LinkedIn / X", Icon: Megaphone, to: "/tools/social-media", badge: "LIVE" },
      { name: "AI Blog Generator", desc: "Long-form SEO articles", Icon: FileText, to: "/ai-blog", badge: "LIVE" },
      { name: "Content Tools", desc: "Email / Newsletter / Product copy", Icon: Mail, to: "/tools/content", badge: "LIVE" },
      { name: "Google Ads Copy", desc: "Multi-variant ad headlines", Icon: Wand2, to: "/tools/google-ads", badge: "LIVE" },
      { name: "Facebook Ads", desc: "Hook + body + CTA combos", Icon: Wand2, to: "/tools/facebook-ads", badge: "LIVE" },
      { name: "AI Image Generator", desc: "Nano Banana + GPT Image 1", Icon: ImageIcon, to: "/ai-image", badge: "LIVE" },
    ],
  },
  {
    title: "Teaching",
    tools: [
      { name: "AI PPT Generator", desc: "Topic → editable PPTX", Icon: GraduationCap, to: "/tools/ai-slides", badge: "LIVE" },
      { name: "SRT Captions", desc: "Generate subtitle files", Icon: FileText, to: "/tools/srt-captions", badge: "LIVE" },
      { name: "Auto Captions", desc: "Video → branded captions", Icon: Mic, to: "/tools/auto-captions", badge: "LIVE" },
      { name: "PDF Tools", desc: "Merge / split / compress", Icon: FileText, to: "/tools/design/pdf", badge: "LIVE" },
      { name: "Design Tools", desc: "Banner / poster / social cards", Icon: Palette, to: "/tools/design", badge: "LIVE" },
      { name: "Lesson Planner", desc: "Topic → 45-min plan", Icon: GraduationCap, to: "/teaching/lesson-planner", badge: "LIVE" },
    ],
  },
  {
    title: "Free utilities",
    tools: [
      { name: "Word Counter", desc: "Words / chars / reading time", Icon: Type, to: "/free/word-counter", badge: "FREE" },
      { name: "URL Shortener", desc: "Short links + analytics", Icon: LinkIcon, to: "/free/url-shortener", badge: "FREE" },
      { name: "Password Generator", desc: "Strong random passwords", Icon: Key, to: "/free/password-generator", badge: "FREE" },
      { name: "ROI Calculator", desc: "Marketing ROI in seconds", Icon: Calculator, to: "/free/roi-calculator", badge: "FREE" },
      { name: "Color Palette", desc: "Brand colors + complementary", Icon: Palette, to: "/free/color-palette-generator", badge: "FREE" },
      { name: "WhatsApp Generator", desc: "Bulk message templates", Icon: MessageSquare, to: "/free/whatsapp-generator", badge: "FREE" },
    ],
  },
];

export default function PremiumAIStudio() {
  return (
    <SuiteHub
      testId="ai-studio"
      eyebrow="AI Studio"
      title="Every AI tool in one workspace"
      subtitle="No more switching between 20 tabs. Pick a goal — we handle the workflow."
      sections={SECTIONS}
    />
  );
}
